import React, { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase, Database } from '../lib/supabase'
import {
  ArrowLeftIcon,
  PhotoIcon,
  CheckIcon,
  XMarkIcon,
  CameraIcon,
  TrashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

type CookieCutter = Database['public']['Tables']['cookie_cutters']['Row']
type StorageBin = Database['public']['Tables']['storage_bins']['Row']

export default function CookieCutterEdit() {
  const [cookieCutter, setCookieCutter] = useState<CookieCutter | null>(null)
  const [storageBins, setStorageBins] = useState<StorageBin[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // Form fields
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [customCategory, setCustomCategory] = useState('')
  const [showCustomCategory, setShowCustomCategory] = useState(false)
  const [binId, setBinId] = useState('')
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [photoRotation, setPhotoRotation] = useState(0)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)

      // Fetch cookie cutter
      const { data: cutterData, error: cutterError } = await supabase
        .from('cookie_cutters')
        .select('*')
        .eq('id', id)
        .eq('user_id', user?.id)
        .single()

      if (cutterError) {
        if (cutterError.code === 'PGRST116') {
          setError('Cookie cutter not found')
        } else {
          console.error('Error fetching cookie cutter:', cutterError)
          setError('Failed to load cookie cutter')
        }
        return
      }

      setCookieCutter(cutterData)
      setName(cutterData.name)
      setDescription(cutterData.description || '')
      setCategory(cutterData.category || '')
      setBinId(cutterData.bin_id || '')

      // Fetch storage bins
      const { data: binsData, error: binsError } = await supabase
        .from('storage_bins')
        .select('*')
        .eq('user_id', user?.id)
        .order('name')

      if (binsError) {
        console.error('Error fetching storage bins:', binsError)
      } else {
        setStorageBins(binsData || [])
      }

      // Fetch user's custom categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('cookie_cutter_categories')
        .select('name')
        .eq('user_id', user?.id)
        .order('name')

      if (categoriesError && categoriesError.code !== 'PGRST116') { // Ignore "relation does not exist" error
        console.error('Error fetching categories:', categoriesError)
        // Fall back to default categories
        setCategories([
          'Holiday',
          'Animals',
          'Letters & Numbers',
          'Basic Shapes',
          'Flowers',
          'Food',
          'Transportation',
          'Sports',
          'Other'
        ])
      } else {
        // Use custom categories if available, otherwise default categories
        const customCategories = categoriesData?.map((cat: { name: string }) => cat.name) || []
        const defaultCategories = [
          'Holiday',
          'Animals',
          'Letters & Numbers',
          'Basic Shapes',
          'Flowers',
          'Food',
          'Transportation',
          'Sports',
          'Other'
        ]

        const allCategories = customCategories.length > 0 ? customCategories : defaultCategories
        setCategories(allCategories)

        // Check if current category is not in the list (custom category)
        const currentCategory = cutterData.category
        if (currentCategory && !allCategories.includes(currentCategory)) {
          setShowCustomCategory(true)
          setCustomCategory(currentCategory)
        }
      }
    } catch (err) {
      console.error('Error:', err)
      setError('Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [id, user?.id])

  useEffect(() => {
    if (id && user) {
      fetchData()
    }
  }, [id, user, fetchData])

  const handleSave = async () => {
    if (!cookieCutter) return
    
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      let photoUrl = cookieCutter.photo_url

      // Upload new photo if one was selected
      if (photoFile) {
        const uploadedUrl = await uploadPhoto(photoFile)
        if (uploadedUrl) {
          photoUrl = uploadedUrl
        } else {
          setError('Failed to upload photo')
          setSaving(false)
          return
        }
      }
      // Handle rotation of existing photo without new file upload
      else if (photoRotation !== 0 && cookieCutter.photo_url) {
        const rotatedUrl = await rotateExistingPhoto(cookieCutter.photo_url)
        if (rotatedUrl) {
          photoUrl = rotatedUrl
        } else {
          setError('Failed to rotate photo')
          setSaving(false)
          return
        }
      }

      const { error: updateError } = await supabase
        .from('cookie_cutters')
        .update({
          name: name.trim(),
          description: description.trim() || null,
          category: category.trim() || null,
          bin_id: binId || null,
          photo_url: photoUrl,
        })
        .eq('id', cookieCutter.id)
        .eq('user_id', user?.id)

      if (updateError) {
        console.error('Error updating cookie cutter:', updateError)
        setError('Failed to save changes')
        return
      }

      setSuccess('Cookie cutter updated successfully!')
      
      // Navigate back to detail view after a short delay
      setTimeout(() => {
        navigate(`/inventory/${cookieCutter.id}`)
      }, 1000)
      
    } catch (err) {
      console.error('Error:', err)
      setError('Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    navigate(`/inventory/${id}`)
  }

  const handleCategoryChange = (selectedCategory: string) => {
    if (selectedCategory === 'custom') {
      setShowCustomCategory(true)
      setCategory('')
    } else {
      setShowCustomCategory(false)
      setCustomCategory('')
      setCategory(selectedCategory)
    }
  }

  const handleCustomCategoryChange = (customCategoryValue: string) => {
    setCustomCategory(customCategoryValue)
    setCategory(customCategoryValue)
  }

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB')
      return
    }

    setPhotoFile(file)
    
    // Create preview URL
    const reader = new FileReader()
    reader.onload = (e) => {
      setPhotoPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
    setError('')
  }

  const handleRemovePhoto = () => {
    setPhotoFile(null)
    setPhotoPreview(null)
    setPhotoRotation(0)
  }

  const handleRotatePhoto = () => {
    setPhotoRotation((prev) => (prev + 90) % 360)
  }

  const rotateImageCanvas = (imageElement: HTMLImageElement, rotation: number): Promise<Blob> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')!
      
      // Set canvas dimensions based on rotation
      if (rotation === 90 || rotation === 270) {
        canvas.width = imageElement.naturalHeight
        canvas.height = imageElement.naturalWidth
      } else {
        canvas.width = imageElement.naturalWidth
        canvas.height = imageElement.naturalHeight
      }
      
      // Move to center of canvas
      ctx.translate(canvas.width / 2, canvas.height / 2)
      
      // Rotate
      ctx.rotate((rotation * Math.PI) / 180)
      
      // Draw image centered
      ctx.drawImage(
        imageElement,
        -imageElement.naturalWidth / 2,
        -imageElement.naturalHeight / 2
      )
      
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob)
        }
      }, 'image/jpeg', 0.9)
    })
  }

  const rotateExistingPhoto = async (imageUrl: string): Promise<string | null> => {
    if (!cookieCutter || !user) return null

    try {
      setUploadingPhoto(true)
      
      // Load the existing image
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.src = imageUrl
      
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
      })
      
      // Apply rotation using canvas
      const rotatedBlob = await rotateImageCanvas(img, photoRotation)
      
      // Create a new file from the rotated blob
      const fileName = `cookie_cutter_${cookieCutter.id}_${Date.now()}.jpg`
      const filePath = `${user.id}/${fileName}`

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('cookie-cutters')
        .upload(filePath, rotatedBlob, {
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        throw uploadError
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('cookie-cutters')
        .getPublicUrl(filePath)

      return urlData.publicUrl
    } catch (err) {
      console.error('Error rotating existing photo:', err)
      throw err
    } finally {
      setUploadingPhoto(false)
    }
  }

  const uploadPhoto = async (file: File): Promise<string | null> => {
    if (!cookieCutter || !user) return null

    try {
      setUploadingPhoto(true)
      
      let fileToUpload = file

      // Apply rotation if needed
      if (photoRotation !== 0) {
        const img = new Image()
        img.src = URL.createObjectURL(file)
        
        await new Promise((resolve) => {
          img.onload = resolve
        })
        
        const rotatedBlob = await rotateImageCanvas(img, photoRotation)
        fileToUpload = new File([rotatedBlob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now()
        })
        
        URL.revokeObjectURL(img.src)
      }
      
      // Generate unique filename
      const fileExt = fileToUpload.name.split('.').pop()
      const fileName = `cookie_cutter_${cookieCutter.id}_${Date.now()}.${fileExt}`
      const filePath = `${user.id}/${fileName}`

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('cookie-cutters')
        .upload(filePath, fileToUpload, {
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        throw uploadError
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('cookie-cutters')
        .getPublicUrl(filePath)

      return urlData.publicUrl
    } catch (err) {
      console.error('Error uploading photo:', err)
      throw err
    } finally {
      setUploadingPhoto(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🍪</div>
          <div className="text-gray-600">Loading cookie cutter...</div>
        </div>
      </div>
    )
  }

  if (error && !cookieCutter) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{error}</h2>
          <p className="text-gray-600 mb-4">
            The cookie cutter you're trying to edit doesn't exist or you don't have permission to edit it.
          </p>
          <Link
            to="/inventory"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Inventory
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <nav className="flex items-center text-sm text-gray-500 mb-4">
          <Link to="/inventory" className="hover:text-gray-700">
            Inventory
          </Link>
          <span className="mx-2">/</span>
          <Link to={`/inventory/${id}`} className="hover:text-gray-700">
            {cookieCutter?.name}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900">Edit</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900">Edit Cookie Cutter</h1>
        <p className="mt-1 text-sm text-gray-500">
          Update the details of your cookie cutter
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Photo */}
        <div className="space-y-4">
          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative">
            {photoPreview || cookieCutter?.photo_url ? (
              <img
                src={photoPreview || cookieCutter?.photo_url}
                alt={cookieCutter?.name || 'Preview'}
                className="w-full h-full object-cover transition-transform duration-300"
                style={{
                  transform: `rotate(${photoRotation}deg)`
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <PhotoIcon className="h-24 w-24 text-gray-400" />
              </div>
            )}
            
            {/* Photo overlay buttons */}
            <div className="absolute bottom-2 right-2 flex space-x-2">
              {(photoPreview || cookieCutter?.photo_url) && (
                <button
                  type="button"
                  onClick={handleRotatePhoto}
                  className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                  title="Rotate photo"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                </button>
              )}
              {(photoPreview || cookieCutter?.photo_url) && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                  title="Remove photo"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          {/* Photo upload section */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cookie Cutter Photo
              </label>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <CameraIcon className="w-8 h-8 mb-2 text-gray-400" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    disabled={saving || uploadingPhoto}
                  />
                </label>
              </div>
            </div>
            
            {photoFile && (
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-blue-50 p-3 rounded-md">
                  <div className="flex items-center space-x-2">
                    <PhotoIcon className="h-5 w-5 text-blue-500" />
                    <span className="text-sm font-medium text-blue-700">{photoFile.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
                
                {/* Rotation controls */}
                {photoPreview && (
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                    <div className="flex items-center space-x-2">
                      <ArrowPathIcon className="h-5 w-5 text-gray-500" />
                      <span className="text-sm text-gray-700">
                        Rotation: {photoRotation}°
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handleRotatePhoto}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors duration-200"
                      >
                        Rotate 90°
                      </button>
                      {photoRotation !== 0 && (
                        <button
                          type="button"
                          onClick={() => setPhotoRotation(0)}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm transition-colors duration-200"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {uploadingPhoto && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">Uploading photo...</p>
              </div>
            )}
          </div>
        </div>

        {/* Edit Form */}
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Cookie Cutter Details</h2>
            
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter cookie cutter name"
                  required
                />
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  id="category"
                  value={showCustomCategory ? 'custom' : category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                  <option value="custom">+ Add new category</option>
                </select>
                
                {showCustomCategory && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={customCategory}
                      onChange={(e) => handleCustomCategoryChange(e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter new category name"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              {/* Storage Bin */}
              <div>
                <label htmlFor="storage-bin" className="block text-sm font-medium text-gray-700">
                  Storage Bin
                </label>
                <select
                  id="storage-bin"
                  value={binId}
                  onChange={(e) => setBinId(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Unassigned</option>
                  {storageBins.map((bin) => (
                    <option key={bin.id} value={bin.id}>
                      {bin.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Add any notes or details about this cookie cutter"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || uploadingPhoto || !name.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                {uploadingPhoto ? 'Uploading Photo...' : saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}